import { Alert, Button, Popconfirm, Table, type TableColumnsType } from 'antd'

import { FormattedDate } from '@/components/common/FormattedDate'
import { UserEmailLink } from '@/components/common/UserEmailLink'

export interface RuleRow {
  key: string
  reason: string
  author: string
  updated_at: string
}

interface Props {
  rows: RuleRow[] | undefined
  loading: boolean
  error: Error | null
  keyTitle: string
  emptyText: string
  canWrite: boolean
  // Names the removal on its confirm button, e.g. «Unblock».
  removeAction: string
  onRemove: (key: string) => Promise<unknown>
}

// Operator sales rules: one row per blocked or excluded key.
export function RulesTable({
  rows,
  loading,
  error,
  keyTitle,
  emptyText,
  canWrite,
  removeAction,
  onRemove,
}: Props) {
  const columns: TableColumnsType<RuleRow> = [
    {
      title: keyTitle,
      dataIndex: 'key',
      render: (key: string) => <code>{key}</code>,
    },
    { title: 'Reason', dataIndex: 'reason' },
    {
      title: 'By',
      dataIndex: 'author',
      width: 220,
      render: (author: string) => <UserEmailLink userId={author} />,
    },
    {
      title: 'Since',
      dataIndex: 'updated_at',
      width: 170,
      render: (date: string) => (
        <FormattedDate date={date} fmt="MMM d, yyyy HH:mm" />
      ),
    },
  ]
  if (canWrite) {
    columns.push({
      key: 'remove',
      width: 120,
      render: (_, row) => (
        <Popconfirm
          title={`${removeAction} ${row.key}?`}
          okText={removeAction}
          onConfirm={() => onRemove(row.key).catch(() => undefined)}
        >
          <Button size="small">{removeAction}</Button>
        </Popconfirm>
      ),
    })
  }

  // A failed read is not «nothing excluded».
  if (error) {
    return <Alert type="error" showIcon message={error.message} />
  }

  return (
    <Table<RuleRow>
      rowKey="key"
      dataSource={rows}
      columns={columns}
      loading={loading}
      pagination={false}
      size="small"
      locale={{ emptyText }}
    />
  )
}
